import React, { useState } from "react";
import { User, Plus, MapPinHouse, Wallet, Users, Phone } from "lucide-react";

const plans = [
  {
    name: "Loyalty Only",
    priceFlat: 12, // flat price, no staff count
    smsIncluded: "1 location",
    description: "For salons who use only Digital Loyalty service",
    features: [
      "Send automated customer reminders with email",
      "Seamless in-store and online instant stamp reward",
      "Card customizations",
      "Data Export",
      "No POS needed",
    ],
  },
  {
    name: "Booking Only",
    pricePerStaff: 15,
    smsIncluded: "1 location and max 20 staffs",
    description: "For salons who want only Booking Staff Management service.",
    features: [
      "Send digital consultation forms",
      "Store client photos and documents",
      "Waitlist to manage appointment requests and cancellations",
      "Automated follow-up messages",
      "Set pricing tiers based on staff skills and seniority",
    ],
  },
  {
    name: "Innovate",
    pricePerStaff: 15,
    loyaltyAddon: 12, // add loyalty
    smsIncluded: "1 location and max 20 staffs",
    description: "For salons who want full experience Booking and Loyalty",
    features: [
      "Everything in Booking and Loyalty, plus:",
      "Direct phone support",
      "Encourage rebooking with automated reminders",
      "Create and track staff sales targets and performance",
      "VIP services ",
      "Send customers to a personalised booking confirmation page",
    ],
  },
];

const PricingPlans = () => {
  const [staffCount, setStaffCount] = useState(1);

  const handleIncrement = () => {
    if (staffCount < 20) setStaffCount(staffCount + 1);
  };

  const handleDecrement = () => {
    if (staffCount > 1) setStaffCount(staffCount - 1);
  };

  const calculateTotal = (plan) => {
    if (plan.name === "Loyalty Only") {
      return plan.priceFlat;
    }

    if (plan.name === "Booking Only") {
      if (staffCount >= 20) return 300;
      if (staffCount >= 6 && staffCount <= 20) return plan.pricePerStaff * 5;
      return plan.pricePerStaff * staffCount;
    }

    if (plan.name === "Innovate") {
      if (staffCount >= 20) return 300 + plan.loyaltyAddon;
      if (staffCount >= 6 && staffCount <= 20)
        return plan.pricePerStaff * 5 + plan.loyaltyAddon;
      return plan.pricePerStaff * staffCount + plan.loyaltyAddon;
    }

    return 0;
  };

  return (
    <div className="min-h-screen bg-black pb-16 pt-32 px-4 text-text">
      <div className="text-center mb-10">
        <h2 className="text-textGray mb-4">Choose your plan</h2>
        <h2 className="text-3xl md:text-4xl font-bold mb-3">Pricing</h2>
        <p className="text-textGray">How many bookable staff?</p>
        <div className="flex justify-center items-center mt-6 space-x-4">
          <button
            onClick={handleDecrement}
            className="bg-drylight border border-border rounded-full w-10 h-10 flex justify-center items-center text-lg font-semibold"
          >
            -
          </button>
          <div className="text-sm font-bold flex items-center border border-border p-3 rounded-xl">
            {staffCount === 20 ? (
              <>
                <Users className="w-4 h-4 mx-1" />
                20+
              </>
            ) : (
              <>
                <User className="w-4 h-4 mx-1" />
                {staffCount}
              </>
            )}
          </div>
          <button
            onClick={handleIncrement}
            className="bg-drylight border border-border rounded-full w-10 h-10 flex justify-center items-center text-lg font-semibold"
          >
            +
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`rounded-2xl p-6 shadow-lg flex flex-col justify-between bg-dry ${
              plan.name === "Innovate"
                ? "border-4 border-redlightlight bg-redlightlight text-border"
                : "border border-border"
            }`}
          >
            <div>
              {plan.name === "Innovate" ? (
                <h3 className="text-xl font-semibold mb-2 flex items-center justify-between">
                  <span className="text-redlight">{plan.name}</span>
                  <span className="ml-3 text-xs font-semibold text-white bg-redlight px-3 py-1 rounded-full border border-reddark flex items-center">
                    ★ Recommended
                  </span>
                </h3>
              ) : (
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
              )}

              {staffCount < 20 || plan.name === "Loyalty Only" ? (
                plan.name === "Innovate" ? (
                  <div className="mb-4">
                    <div className="flex">
                      <span className="text-3xl font-bold">
                        $
                        {staffCount >= 6 && staffCount < 20
                          ? 99
                          : calculateTotal(plan)}
                      </span>
                      <div className="flex flex-col items-start ml-2">
                        <span className="ml-1 text-[12px]">billed</span>
                        <span className="ml-1 text-[12px]">monthly</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1 text-sm mb-4 mt-2">
                      <span className="flex items-center">
                        • <Wallet className="w-4 h-4 mx-1" /> $
                        {staffCount >= 6 && staffCount < 20
                          ? 990
                          : calculateTotal(plan) * 10}
                        <span className="text-xs font-normal">
                          (exc) GST pay upfront for a year
                        </span>
                      </span>
                      <span className="flex items-center text-[12px]">
                        • <User className="w-4 h-4 mx-1" />
                        {staffCount >= 6 && staffCount < 20
                          ? "Flat rate for 6–20 staff"
                          : `${staffCount} ${
                              staffCount === 1 ? "staff" : "staff members"
                            }`}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 flex">
                      <span className="text-3xl font-bold">
                        $
                        {plan.name === "Loyalty Only"
                          ? plan.priceFlat
                          : calculateTotal(plan)}
                      </span>
                      <div className="flex flex-col items-start ml-2">
                        <span className="ml-1 text-[12px] text-textGray">
                          billed
                        </span>
                        <span className="ml-1 text-[12px] text-textGray">
                          monthly
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1 text-sm mb-4 mt-2 text-textGray">
                      <span className="flex items-center">
                        • <Wallet className="w-4 h-4 mx-1" /> $
                        {calculateTotal(plan) * 10}
                        <span className="text-xs">
                          (exc) GST pay upfront for a year
                        </span>
                      </span>
                      <span className="flex items-center text-[12px]">
                        {plan.name === "Loyalty Only" ? (
                          <>
                            • <MapPinHouse className="w-4 h-4 mx-1" /> 1
                            location
                          </>
                        ) : (
                          <>
                            • <User className="w-4 h-4 mx-1" />
                            {staffCount >= 6 && staffCount < 20
                              ? "Flat rate for 6–20 staff"
                              : `${staffCount} ${
                                  staffCount === 1 ? "staff" : "staff members"
                                }`}
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                )
              ) : (
                <div className="my-10">
                  <button
                    onClick={() =>
                      (window.location.href = "/contact/book-a-call")
                    }
                    className="w-full py-2 text-sm font-semibold hover:text-redlight flex items-center justify-center gap-2"
                  >
                    <span>Book a call</span>
                    <Phone className="w-4 h-4 text-redlight" />
                    <span>for a custom quote</span>
                  </button>
                </div>
              )}

              <p className="mb-4 text-sm">{plan.description}</p>
              <div className="mb-4">
                <div className="text-sm text-redlight font-semibold flex items-center mb-2">
                  <Plus className="mr-2 " size={16} />
                  Best Deals service included
                </div>
                <div className="text-sm text-redlight font-semibold flex items-center mb-2">
                  <Plus className="mr-2 " size={16} />
                  {plan.smsIncluded}
                </div>
                <div className="text-sm text-redlight font-semibold flex items-center mb-2">
                  <Plus className="mr-2 " size={16} />
                  {plan.name === "Loyalty Only"
                    ? "Unlimited Users subcribes"
                    : "Unlimited Users subcribes"}{" "}
                </div>
                <div className="text-sm text-redlight font-semibold flex items-center">
                  <Plus className="mr-2 " size={16} />
                  {plan.name === "Loyalty Only"
                    ? "Unlimited rewards"
                    : "Unlimited booking"}{" "}
                </div>
              </div>

              <div className="my-6">
                <button
                  className={`w-full py-3 rounded-full font-semibold text-white ${
                    plan.name === "Innovate"
                      ? "bg-redlight border border-textGray"
                      : "bg-drylight border border-boder hover:text-redlight hover:border-redlight"
                  }`}
                >
                  Try 30-day free →
                </button>
              </div>

              <ul className="text-sm space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-redlight font-bold mr-2">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingPlans;
